import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { TenantAdminSummaryResponse, TenantStatus } from '../api';

interface TenantActionsMenuProps {
  tenant: TenantAdminSummaryResponse;
  onChangeStatus: (id: string, status: TenantStatus) => void;
  onChangeLicense: (tenant: TenantAdminSummaryResponse) => void;
  onView: (id: string) => void;
}

// Dropdown is rendered through a portal with `position: fixed` so it escapes the
// `overflow-hidden` of the parent `card` wrapper around the tenants table.
export function TenantActionsMenu({ tenant, onChangeStatus, onChangeLicense, onView }: TenantActionsMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = (): void => setOpen(false);

  useLayoutEffect(() => {
    if (!open || triggerRef.current === null) return;

    function place(): void {
      const rect = triggerRef.current!.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    place();

    // Reposition when the user scrolls or resizes; cheap and avoids stale coords.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        aria-label="Acciones"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
          <circle cx="12" cy="5" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>

      {open
        ? createPortal(
            <>
              <div className="fixed inset-0 z-40" onClick={close} />
              <div
                role="menu"
                className="fixed z-50 w-44 rounded-lg border border-slate-200/70 bg-white py-1 shadow-lg"
                style={{ top: coords.top, right: coords.right }}
              >
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                  onClick={() => { onView(tenant.tenantId); close(); }}
                >
                  Ver detalles
                </button>
                {tenant.status !== 'Suspended' ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-amber-700 hover:bg-amber-50"
                    onClick={() => { onChangeStatus(tenant.tenantId, 'Suspended'); close(); }}
                  >
                    Suspender
                  </button>
                ) : null}
                {tenant.status !== 'Active' ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-50"
                    onClick={() => { onChangeStatus(tenant.tenantId, 'Active'); close(); }}
                  >
                    Activar
                  </button>
                ) : null}
                {tenant.status !== 'Blocked' ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-rose-700 hover:bg-rose-50"
                    onClick={() => { onChangeStatus(tenant.tenantId, 'Blocked'); close(); }}
                  >
                    Bloquear
                  </button>
                ) : null}
                <div className="my-1 border-t border-slate-100" />
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                  onClick={() => { onChangeLicense(tenant); close(); }}
                >
                  Cambiar plan
                </button>
              </div>
            </>,
            document.body,
          )
        : null}
    </>
  );
}
